import React from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionMessage, StepInfo } from '../types/protocol';
import { FieldRenderer } from './fields/FieldRenderer';

interface ActiveFieldProps {
  question: QuestionMessage | null;
  onSubmit: (value: unknown) => void;
  onSearch?: (stepId: string, query: string) => void;
  steps?: StepInfo[];
  onNavigate?: (stepId: string) => void;
  previewData?: unknown;
}

export function ActiveField({ question, onSubmit, onSearch, steps, onNavigate, previewData }: ActiveFieldProps): React.ReactElement | null {
  const { t } = useTranslation();
  // Final confirm steps (Review & Create) render through RecipePreview, not here.
  // But iteration confirm steps (slide review) with reviewData should render.
  if (!question || (question.fieldType === 'confirm' && !question.reviewData)) {
    return null;
  }

  let prevStepId: string | null = null;
  let nextStepId: string | null = null;

  if (steps && steps.length > 0 && onNavigate) {
    const currentIndex = steps.findIndex((s) => s.id === question.stepId);
    if (currentIndex > 0) {
      prevStepId = steps[currentIndex - 1]!.id;
    }
    if (currentIndex >= 0 && currentIndex < steps.length - 1) {
      nextStepId = steps[currentIndex + 1]!.id;
    }
  }

  const showNav = onNavigate && steps && steps.length > 0;

  return (
    <div className="sp-active-field">
      <div className="sp-active-field-header">
        {showNav && (
          <button
            type="button"
            className="sp-step-nav-btn"
            disabled={!prevStepId}
            onClick={() => prevStepId && onNavigate(prevStepId)}
            aria-label={t('steps.previousStep')}
          >
            &#8249;
          </button>
        )}
        <div className="sp-active-field-label">{question.label ?? question.field}</div>
        {showNav && (
          <button
            type="button"
            className="sp-step-nav-btn"
            disabled={!nextStepId}
            onClick={() => nextStepId && onNavigate(nextStepId)}
            aria-label={t('steps.nextStep')}
          >
            &#8250;
          </button>
        )}
      </div>
      <FieldRenderer key={question.stepId} question={question} onSubmit={onSubmit} onSearch={onSearch} previewData={previewData} />
    </div>
  );
}
